import type { ClientSummary } from "@patent-studio/api-types";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { plural } from "@/lib/plural";

/** What goes with the client, naming only the counts it has: "2 drafts in progress and 1 completed draft". */
export function whatGoes(inProgress: number, completed: number): string | null {
  const parts = [
    inProgress > 0 && `${plural(inProgress, "draft")} in progress`,
    completed > 0 && plural(completed, "completed draft"),
  ].filter((part): part is string => part !== false);
  return parts.length === 0 ? null : parts.join(" and ");
}

/**
 * Asks before a client goes, naming what goes with it: its drafts in progress
 * and completed, and everything they hold. A draft with work running would
 * refuse the delete, so the dialog names it before the attorney confirms.
 */
export function DeleteClientDialog({
  client,
  onClose,
  onConfirm,
}: {
  client: ClientSummary;
  onClose: () => void;
  /** Runs the delete. The page owns it, since this dialog is gone before the answer comes. */
  onConfirm: () => void;
}) {
  const running = client.drafts_running;
  const goes = whatGoes(client.drafts_in_progress, client.drafts_completed);
  return (
    <ConfirmDialog
      open
      destructive
      title={`Delete ${client.name}?`}
      actionLabel="Delete client"
      description={
        <>
          {goes === null
            ? "This deletes the client, which has no drafts yet."
            : `This deletes the client with its ${goes}, and everything they hold.`}{" "}
          It cannot be undone.
          {running.length > 0 && (
            <span className="mt-2 block font-semibold text-destructive-strong">
              Work is still running on{" "}
              {running.map((draft) => `"${draft.title}"`).join(", ")}. Wait for
              it to finish, or the delete will be refused.
            </span>
          )}
        </>
      }
      onCancel={onClose}
      onConfirm={() => {
        onClose();
        onConfirm();
      }}
    />
  );
}
